import React, { useContext, useEffect, useRef, useState } from 'react';
import { Pressable, ScrollView, StyleSheet, TextInput, View } from 'react-native';
import { StatusBar } from 'expo-status-bar';
import { SafeAreaInsetsContext, SafeAreaView } from 'react-native-safe-area-context';
import { Text } from '../components/Texte';
import { Eye, EyeOff } from '../components/icons';
import { BrandLogo } from '../components/BrandLogo';
import { FondNuit } from '../components/FondNuit';
import { GradientButton } from '../components/GradientButton';
import { GradientText } from '../components/GradientText';
import { useAuth } from '../hooks/useAuth';
import { useGabarit } from '../hooks/useGabarit';
import { MIN_PASSWORD_LENGTH, validatePassword } from '../utils/validators';
import { authErrorText } from '../utils/authErrors';
import { colonneFormulaire, colors, formes, gradients, layout, screenPadding, spacing, typography } from '../theme/tokens';
import { NOM_MARQUE } from '../config/marque';
import { t } from '../i18n';

const HAUTEUR_BANDEAU = 300;

/**
 * « Nouveau mot de passe » : l'écran sur lequel retombe la personne qui a ouvert le lien reçu par
 * e-mail. La session de récupération existe déjà, mais RootNavigator garde cet écran tant que
 * `recoveryPending` est vrai : on n'entre pas dans l'application sans avoir choisi un mot de passe
 * ni annulé. « Annuler » déconnecte, ce qui ramène à l'écran de connexion.
 */
export function NewPasswordScreen() {
  const { updatePassword, signOut } = useAuth();
  const [password, setPassword] = useState('');
  const [confirmation, setConfirmation] = useState('');
  const [afficher, setAfficher] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [champActif, setChampActif] = useState<'nouveau' | 'confirmation' | null>(null);
  const confirmationRef = useRef<TextInput>(null);
  const basSur = useContext(SafeAreaInsetsContext)?.bottom ?? 0;
  const { large } = useGabarit();

  // En cas de succès, le verrou tombe et cet écran est démonté : on n'écrit plus dans son état.
  const monte = useRef(true);
  useEffect(() => {
    monte.current = true;
    return () => {
      monte.current = false;
    };
  }, []);

  // Garde contre la double soumission (touche « terminé » du clavier puis bouton) : le second
  // `updateUser` recevrait `same_password` et ferait clignoter une erreur après un succès.
  const enCours = useRef(false);

  const submit = async () => {
    if (enCours.current) return;
    setError(null);
    if (!validatePassword(password).valid) {
      setError(t('auth.newPassword.tooShort', { min: MIN_PASSWORD_LENGTH }));
      return;
    }
    if (password !== confirmation) {
      setError(t('auth.newPassword.mismatch'));
      return;
    }
    enCours.current = true;
    setLoading(true);
    try {
      const result = await updatePassword(password);
      if (result.errorKey && monte.current) setError(authErrorText(result.errorKey));
    } finally {
      enCours.current = false;
      if (monte.current) setLoading(false);
    }
  };

  // Si la déconnexion échoue, l'écran reste : on le dit au lieu d'un bouton qui ne répond pas.
  const annuler = async () => {
    setError(null);
    const result = await signOut();
    if (result.errorKey && monte.current) setError(authErrorText(result.errorKey));
  };

  const titreStyle = large ? { ...styles.titre, ...styles.titreL } : styles.titre;
  const sEntete = (
    <>
        <View style={[styles.entete, colonneFormulaire]}>
          <View style={[styles.marque, large && styles.marqueL]}>
            <BrandLogo variant="mark" size={28} testID="newpwd-logo" />
            <Text style={styles.marqueNom}>{NOM_MARQUE}</Text>
          </View>
          <Text style={titreStyle}>{t('auth.newPassword.title')}</Text>
          <View style={styles.accent}>
            <GradientText colors={gradients.surNuit} style={titreStyle}>
              {t('auth.newPassword.titleAccent')}
            </GradientText>
          </View>
          <Text style={[styles.sousTitre, large && styles.sousTitreL]}>{t('auth.newPassword.subtitle')}</Text>
        </View>

    </>
  );
  const sFeuille = (
    <>
        <View style={[styles.feuille, large && styles.feuilleL, { paddingBottom: spacing.xl + 6 + basSur }]}>
          <View style={[styles.formulaire, colonneFormulaire]}>
            <View style={[styles.champ, styles.champLigne, champActif === 'nouveau' && styles.champActif]}>
              <View style={styles.champCorps}>
                <Text style={styles.etiquette} importantForAccessibility="no">
                  {t('auth.newPassword.newLabel')}
                </Text>
                <TextInput
                  testID="newpwd-password"
                  style={styles.saisie}
                  accessibilityLabel={t('auth.newPassword.newLabel')}
                  placeholder="••••••••"
                  placeholderTextColor={colors.grey400}
                  autoCapitalize="none"
                  autoComplete="new-password"
                  textContentType="newPassword"
                  secureTextEntry={!afficher}
                  returnKeyType="next"
                  onSubmitEditing={() => confirmationRef.current?.focus()}
                  value={password}
                  onChangeText={setPassword}
                  onFocus={() => setChampActif('nouveau')}
                  onBlur={() => setChampActif(null)}
                />
              </View>
              <Pressable
                testID="newpwd-toggle"
                accessibilityRole="button"
                accessibilityLabel={afficher ? t('auth.hidePassword') : t('auth.showPassword')}
                onPress={() => setAfficher((v) => !v)}
                style={styles.oeil}
              >
                {afficher ? (
                  <EyeOff size={20} color={colors.grey500} strokeWidth={2} />
                ) : (
                  <Eye size={20} color={colors.grey500} strokeWidth={2} />
                )}
              </Pressable>
            </View>

            <View style={[styles.champ, champActif === 'confirmation' && styles.champActif]}>
              <Text style={styles.etiquette} importantForAccessibility="no">
                {t('auth.newPassword.confirmLabel')}
              </Text>
              <TextInput
                ref={confirmationRef}
                testID="newpwd-confirm"
                style={styles.saisie}
                accessibilityLabel={t('auth.newPassword.confirmLabel')}
                placeholder="••••••••"
                placeholderTextColor={colors.grey400}
                autoCapitalize="none"
                autoComplete="new-password"
                textContentType="newPassword"
                secureTextEntry={!afficher}
                returnKeyType="done"
                onSubmitEditing={submit}
                value={confirmation}
                onChangeText={setConfirmation}
                onFocus={() => setChampActif('confirmation')}
                onBlur={() => setChampActif(null)}
              />
            </View>

            {error ? (
              <Text testID="newpwd-error" accessibilityRole="alert" accessibilityLiveRegion="polite" style={styles.error}>
                {error}
              </Text>
            ) : null}

            <GradientButton
              label={t('auth.newPassword.cta')}
              withArrow
              variante="cta"
              hauteur="lg"
              loading={loading}
              onPress={submit}
              testID="newpwd-submit"
            />

            <Pressable
              testID="newpwd-cancel"
              accessibilityRole="button"
              onPress={() => {
                void annuler();
              }}
              hitSlop={8}
              style={styles.lien}
            >
              <Text style={styles.lienTexte}>{t('auth.newPassword.cancel')}</Text>
            </Pressable>
          </View>
        </View>
    </>
  );

  return (
    <SafeAreaView testID="newpwd-screen" style={styles.container} edges={['top']}>
      <StatusBar style="light" />
      {large ? (
        <View testID="newpwd-split" style={styles.splitL}>
          <View style={styles.panneauMarqueL}>
            <FondNuit style={StyleSheet.absoluteFill} />
            <View style={styles.marqueContenuL}>{sEntete}</View>
          </View>
          <ScrollView
            style={styles.panneauFormL}
            contentContainerStyle={styles.panneauFormContenuL}
            keyboardShouldPersistTaps="handled"
            showsVerticalScrollIndicator={false}
          >
            {sFeuille}
          </ScrollView>
        </View>
      ) : (
        <>
          <FondNuit style={styles.bandeau} />
          <ScrollView contentContainerStyle={styles.content} keyboardShouldPersistTaps="handled" showsVerticalScrollIndicator={false}>
            {sEntete}
            {sFeuille}
          </ScrollView>
        </>
      )}
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  // Écran large : même découpage que la connexion (panneau de marque à gauche, formulaire à droite).
  splitL: { flex: 1, flexDirection: 'row', width: '100%' },
  panneauMarqueL: { flex: 1, minWidth: 0, backgroundColor: colors.night, justifyContent: 'center', overflow: 'hidden' },
  marqueContenuL: { width: '100%', maxWidth: layout.formMaxWidth + spacing.xxl * 2, alignSelf: 'center', padding: spacing.xxl },
  panneauFormL: { flex: 1, minWidth: 0, backgroundColor: colors.grey50 },
  panneauFormContenuL: { flexGrow: 1, justifyContent: 'center', padding: spacing.xxl },
  feuilleL: { flexGrow: 0, borderRadius: 0, backgroundColor: 'transparent', paddingTop: 0, paddingHorizontal: 0 },
  marqueL: { marginBottom: spacing.xxl * 2 },
  titreL: { fontSize: 52, lineHeight: 58, letterSpacing: -1.5 },
  sousTitreL: { fontSize: 18, lineHeight: 26, marginTop: spacing.lg },
  container: { flex: 1, backgroundColor: colors.night },
  bandeau: { position: 'absolute', left: 0, right: 0, top: 0, height: HAUTEUR_BANDEAU },
  content: { flexGrow: 1 },

  entete: { paddingHorizontal: screenPadding + spacing.sm, paddingTop: spacing.md, paddingBottom: spacing.xl },
  marque: { flexDirection: 'row', alignItems: 'center', gap: spacing.sm, marginBottom: spacing.xl },
  marqueNom: { fontSize: 19, lineHeight: 24, fontWeight: '800', color: colors.white },
  accent: { alignSelf: 'flex-start' },
  titre: { fontSize: 32, lineHeight: 38, fontWeight: '800', letterSpacing: -0.8, color: colors.white },
  sousTitre: { marginTop: 10, fontSize: 15, lineHeight: 21, color: colors.blancAtenue },

  feuille: {
    flexGrow: 1,
    backgroundColor: colors.grey50,
    borderTopLeftRadius: formes.rayon.xl,
    borderTopRightRadius: formes.rayon.xl,
    paddingTop: 22,
    paddingHorizontal: screenPadding + spacing.sm,
  },
  formulaire: { gap: 14 },

  champ: {
    gap: 6,
    paddingVertical: 11,
    paddingHorizontal: spacing.lg,
    borderRadius: formes.rayon.md,
    backgroundColor: colors.white,
    borderWidth: 1.5,
    borderColor: colors.grey200,
  },
  champActif: { borderColor: colors.electric },
  champLigne: { flexDirection: 'row', alignItems: 'center', gap: 10, paddingEnd: spacing.sm },
  champCorps: { flex: 1, gap: 6 },
  etiquette: { fontSize: 12, fontWeight: '700', color: colors.grey500 },
  saisie: { padding: 0, fontSize: typography.body.size + 1, color: colors.grey900 },
  oeil: {
    width: formes.cible,
    height: formes.cible,
    borderRadius: formes.rayon.sm,
    alignItems: 'center',
    justifyContent: 'center',
  },
  error: { color: colors.error, fontSize: typography.small.size },
  lien: { alignSelf: 'center', minHeight: formes.cible, justifyContent: 'center' },
  lienTexte: { fontSize: typography.small.size, fontWeight: '700', color: colors.electricDeep },
});
